function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH22_PART11: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 126 — Customer Advocacy Nomination (Marketo, Pattern J) ────────────

  "Customer Advocacy Nomination": {
    nodes: [
      trigger(
        "Webhook trigger: NPS promoter detected in Marketo",
        "webhook_app",
        {
          source: "marketo",
          event: "nps_promoter_detected",
          howItStarts:
            "Fires when a customer submits an NPS score of 9 or 10 in a Marketo survey, indicating they are a strong promoter and potential advocacy candidate.",
          setup:
            "Connect Marketo to send a webhook when an NPS response with score >= 9 is captured. The payload includes lead ID, NPS score, company, and survey context.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Marketo: Lookup customer health and prior advocacy",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{marketo_instance}.mktorest.com/rest/v1/leads/{lead_id}.json?fields=firstName,lastName,email,company,npsScore,customerSince,totalRevenue,lastAdvocacyAsk,advocacyCount,caseStudyParticipant",
            headers: [
              { key: "Authorization", value: "Bearer {marketo_token}" },
            ],
            body: [],
            variables: [
              {
                name: "result[0].firstName",
                customName: "first_name",
                type: "string",
              },
              { name: "result[0].email", customName: "email", type: "string" },
              {
                name: "result[0].company",
                customName: "company",
                type: "string",
              },
              {
                name: "result[0].totalRevenue",
                customName: "total_revenue",
                type: "number",
              },
              {
                name: "result[0].lastAdvocacyAsk",
                customName: "last_advocacy_ask",
                type: "string",
              },
              {
                name: "result[0].advocacyCount",
                customName: "advocacy_count",
                type: "number",
              },
              {
                name: "result[0].caseStudyParticipant",
                customName: "case_study_participant",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "Eligible: not asked in last 90 days and advocacy count < 3?",
        520,
        {
          conditionDetails: {
            rules: [
              {
                variable: "last_advocacy_ask",
                operator: "is_before",
                value: "90_days_ago",
              },
              { variable: "advocacy_count", operator: "less_than", value: "3" },
            ],
            combinator: "and",
          },
        },
      ),
      emailNode(
        "4",
        "Email: Invite customer to case study or testimonial",
        760,
        "{email}",
        "We'd love to tell your story, {first_name}",
        "Hi {first_name},\n\nThank you for the amazing NPS feedback! Your experience with our platform at {company} is inspiring, and we'd love to share it.\n\nWe're looking for customers to participate in a short case study — just a 20-minute conversation about how you use the product. Participants receive a $100 gift card and featured placement on our website.\n\nWould you be open to a quick chat? Simply reply to this email or click the link below to schedule a time.\n\n[Schedule Case Study Interview]\n\nThank you for being such a valued customer!\n\nBest regards,\nCustomer Marketing Team",
      ),
      n("5", "delayAction", "Wait 5 days for response", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("6", "condition", "Accepted or replied to advocacy ask?", 1240, {
        conditionDetails: {
          rules: [
            { variable: "email_replied", operator: "equals", value: "true" },
          ],
          combinator: "or",
        },
      }),
      emailNode(
        "7",
        "Email: Gentle reminder with alternate advocacy option",
        1480,
        "{email}",
        "Quick follow-up: share your story in 5 minutes, {first_name}",
        "Hi {first_name},\n\nI wanted to follow up on our case study invitation. If a full interview isn't the right fit, we'd also love a short written testimonial or even a 1-paragraph quote we can feature.\n\nAlternatively, you can leave a review on G2 — it only takes 5 minutes and helps other teams like yours find the right solution.\n\n[Leave a G2 Review] | [Submit a Written Quote]\n\nEither way, thank you for being a champion!\n\nBest,\nCustomer Marketing Team",
        380,
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log advocacy nomination and outcome",
        1720,
        "AdvocacyLog",
        '[["{email}","{company}","{total_revenue}","NPS Promoter","{advocacy_count}","{case_study_participant}","{today}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "8", "output_1"),
      e("7", "8"),
    ],
    summary:
      "When a Marketo NPS survey identifies a promoter (score 9-10), this workflow checks advocacy eligibility (not asked recently, under participation cap), sends a case study invitation with incentive, waits 5 days, then follows up with an easier alternative (G2 review or written quote) if they haven't responded. Every nomination is logged to Google Sheets for tracking advocacy pipeline conversion.",
  },

  // ─── 127 — New Subscriber Welcome Workflow (HubSpot, Pattern A) ─────────

  "New Subscriber Welcome Workflow": {
    nodes: [
      trigger(
        "Webhook trigger: New subscriber via HubSpot form",
        "webhook_app",
        {
          source: "hubspot",
          event: "form_submission.subscribe",
          howItStarts:
            "Fires when a new contact subscribes through a HubSpot form — newsletter signup, blog subscription, or resource opt-in — creating a new contact record.",
          setup:
            "Connect HubSpot to send a webhook on form submission for subscription forms. The payload includes contact email, form ID, and submission source.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract subscriber details and signup context",
        280,
        "You are a subscriber onboarding analyst. Extract structured subscriber details from the webhook payload: email, first_name, subscription_type (newsletter/blog/resource), signup_source (page URL or form name), and inferred_interest based on the form context.",
        "Parse this HubSpot form submission webhook: {webhook_payload}",
        [
          { field: "email", as: "email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          {
            field: "subscription_type",
            as: "subscription_type",
            type: "string",
          },
          { field: "signup_source", as: "signup_source", type: "string" },
          {
            field: "inferred_interest",
            as: "inferred_interest",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "HubSpot: Lookup existing contact profile", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,lifecyclestage,hs_lead_status,company,num_associated_deals",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "properties.hs_lead_status",
              customName: "lead_status",
              type: "string",
            },
            {
              name: "properties.company",
              customName: "company",
              type: "string",
            },
            {
              name: "properties.num_associated_deals",
              customName: "deal_count",
              type: "number",
            },
          ],
        },
      }),
      n("4", "condition", "Is this an existing customer or known lead?", 760, {
        conditionDetails: {
          rules: [
            {
              variable: "lifecycle_stage",
              operator: "equals",
              value: "customer",
            },
          ],
          combinator: "or",
        },
      }),
      emailNode(
        "5",
        "Email: Welcome new subscriber with top content picks",
        1000,
        "{email}",
        "Welcome aboard, {first_name}! Here's what to expect",
        "Hi {first_name},\n\nThanks for subscribing! You've just joined thousands of marketers who get our best insights delivered straight to their inbox.\n\nHere's what you can expect:\n- Weekly curated content on {inferred_interest}\n- Exclusive guides and templates\n- Early access to webinars and events\n\nTo get you started, here are our 3 most popular resources:\n1. [The Complete Guide to {inferred_interest}]\n2. [2025 Industry Benchmark Report]\n3. [Expert Playbook: From Strategy to Execution]\n\nHappy reading!\nThe Content Team",
      ),
      n("6", "delayAction", "Wait 48 hours", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n(
        "7",
        "condition",
        "Has subscriber opened welcome email or clicked a link?",
        1480,
        {
          conditionDetails: {
            rules: [
              { variable: "email_opened", operator: "equals", value: "true" },
            ],
            combinator: "or",
          },
        },
      ),
      emailNode(
        "8",
        "Email: Engagement nudge or deeper content CTA",
        1720,
        "{email}",
        "{first_name}, explore our most actionable resource",
        "Hi {first_name},\n\nWe noticed you're getting settled in — great to have you! We wanted to share one resource that our subscribers consistently rate as the most actionable:\n\n[Download: The {inferred_interest} Playbook — 30 Tactics You Can Use This Week]\n\nThis guide has helped teams increase their results by an average of 40%. It pairs perfectly with the weekly insights you'll receive.\n\nAlso, did you know you can customize your email preferences? [Update your preferences here] to get exactly the content that matters to you.\n\nBest,\nThe Content Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_2"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
      e("7", "8", "output_1"),
    ],
    summary:
      "When a new contact subscribes through a HubSpot form, this workflow extracts subscriber details via AI, checks their existing lifecycle stage, sends a personalized welcome email with top content picks tailored to their inferred interest, waits 48 hours, then sends a follow-up engagement nudge with a high-value playbook and preference center link to drive activation.",
  },

  // ─── 128 — Lead Nurture by Lifecycle Stage (HubSpot, Pattern I) ─────────

  "Lead Nurture by Lifecycle Stage": {
    nodes: [
      trigger(
        "Webhook trigger: HubSpot lifecycle stage change",
        "webhook_app",
        {
          source: "hubspot",
          event: "contact.propertyChange.lifecyclestage",
          howItStarts:
            "Fires when a contact's lifecycle stage changes in HubSpot — from subscriber to lead, lead to MQL, or MQL to SQL — triggering stage-appropriate nurture content.",
          setup:
            "Connect HubSpot to send a webhook on lifecycle stage property change events. The payload includes contact ID, old stage, new stage, and change timestamp.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Determine nurture content strategy for new stage",
        280,
        "You are a B2B lifecycle marketing strategist. Given a contact's new lifecycle stage, company context, and content engagement history, recommend the best nurture content type (educational, case_study, product_demo, roi_calculator, comparison_guide), content_topic, email_tone (informative, consultative, persuasive), and next_stage_cta.",
        "Contact moved to stage: {new_lifecycle_stage}. Company: {company}. Industry: {industry}. Previous content engaged: {content_history}. Recommend nurture strategy.",
        [
          { field: "content_type", as: "content_type", type: "string" },
          { field: "content_topic", as: "content_topic", type: "string" },
          { field: "email_tone", as: "email_tone", type: "string" },
          { field: "next_stage_cta", as: "next_stage_cta", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch contact profile and content history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,company,industry,lifecyclestage,recent_conversion_event_name,hs_analytics_num_page_views,hs_email_last_click_date",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "properties.email", customName: "email", type: "string" },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "properties.lifecyclestage",
                customName: "new_lifecycle_stage",
                type: "string",
              },
              {
                name: "properties.recent_conversion_event_name",
                customName: "last_conversion",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is contact now an MQL or SQL (high-intent stage)?",
        760,
        {
          conditionDetails: {
            rules: [
              {
                variable: "new_lifecycle_stage",
                operator: "is_one_of",
                value: "marketingqualifiedlead,salesqualifiedlead",
              },
            ],
            combinator: "or",
          },
        },
      ),
      emailNode(
        "5",
        "Email: Stage-appropriate nurture content",
        1000,
        "{email}",
        "{first_name}, your next step: {content_topic}",
        "Hi {first_name},\n\nBased on your journey so far, we think this resource will be especially valuable for you right now:\n\n[{content_type}: {content_topic}]\n\nThis {content_type} covers the exact challenges teams at {company} face when evaluating solutions in the {industry} space. It includes real data, practical frameworks, and actionable next steps.\n\nKey takeaways:\n- How leading {industry} companies approach this challenge\n- A step-by-step framework you can implement this quarter\n- Real ROI numbers from similar organizations\n\n{next_stage_cta}\n\nBest,\nThe Marketing Team",
      ),
      n("6", "delayAction", "Wait 4 days", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n("7", "condition", "Has contact engaged with nurture content?", 1480, {
        conditionDetails: {
          rules: [
            { variable: "email_clicked", operator: "equals", value: "true" },
          ],
          combinator: "or",
        },
      }),
      emailNode(
        "8",
        "Email: Progressive content pushing toward next lifecycle stage",
        1720,
        "{email}",
        "{first_name}, see how {industry} teams are driving results",
        "Hi {first_name},\n\nTeams like yours at {company} are seeing measurable impact from the strategies covered in our recent content. Here's the logical next step in your evaluation:\n\n[Case Study: How a {industry} Company Achieved 3x ROI]\n\nThis case study walks through the exact process — from initial challenge to implementation to results. It's the most-shared resource among professionals in your position.\n\nReady to explore what this could look like for {company}?\n\n[Book a 15-Minute Strategy Call] | [See the Interactive ROI Calculator]\n\nLooking forward to helping you move forward.\n\nBest,\nThe Marketing Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
    summary:
      "When a HubSpot contact's lifecycle stage changes, this workflow uses AI to determine the optimal nurture content strategy for the new stage, fetches the contact's profile and engagement history, sends stage-appropriate content (educational for leads, case studies for MQLs, ROI-focused for SQLs), waits 4 days, then delivers progressive content pushing them toward the next lifecycle stage with relevant CTAs.",
  },

  // ─── 129 — Lead Scoring and MQL Promotion (HubSpot, Pattern K) ──────────

  "Lead Scoring and MQL Promotion": {
    nodes: [
      trigger(
        "Webhook trigger: HubSpot high-intent behavior signal",
        "webhook_app",
        {
          source: "hubspot",
          event: "contact.activity.high_intent",
          howItStarts:
            "Fires when a HubSpot contact performs a high-intent action — pricing page visit, demo video watched, multiple content downloads in a session, or repeated visits to product pages.",
          setup:
            "Connect HubSpot to send a webhook on tracked high-intent behaviors. Configure scoring triggers for pricing page views, demo video plays, multi-download sessions, and product page revisits.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch contact fit data and engagement history",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,industry,annualrevenue,hs_analytics_num_page_views,hs_analytics_num_visits,hs_email_open,hubspot_score,lifecyclestage",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "properties.email", customName: "email", type: "string" },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.lastname",
                customName: "last_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "properties.annualrevenue",
                customName: "annual_revenue",
                type: "string",
              },
              {
                name: "properties.hubspot_score",
                customName: "current_score",
                type: "number",
              },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "runCode", "Compute lead score from behavior + fit signals", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Behavior score: page views, visits, email engagement, high-intent action
const behaviorScore = Math.min(50, (parseInt(page_views || '0') * 2) + (parseInt(visits || '0') * 3) + (email_opens > 3 ? 15 : 5) + 10);
// Fit score: job title seniority, company revenue, industry match
const titleWeight = /director|vp|head|chief|cmo|cto/i.test(job_title || '') ? 25 : (/manager|lead/i.test(job_title || '') ? 15 : 5);
const revenueWeight = parseInt(annual_revenue || '0') > 10000000 ? 20 : (parseInt(annual_revenue || '0') > 1000000 ? 10 : 5);
const fitScore = Math.min(50, titleWeight + revenueWeight + 5);
const totalScore = behaviorScore + fitScore;
const mqReady = totalScore >= 70;
return { totalScore, behaviorScore, fitScore, mqReady, scoreLabel: mqReady ? 'MQL' : 'Nurture' };`,
        },
      }),
      n("4", "condition", "Lead score >= 70 (MQL threshold)?", 760, {
        conditionDetails: {
          rules: [{ variable: "mqReady", operator: "equals", value: "true" }],
          combinator: "and",
        },
      }),
      slackNode(
        "5",
        "Slack: Alert sales team about new MQL",
        1000,
        "🎯 *New MQL Alert*\n*Contact:* {first_name} {last_name} ({email})\n*Company:* {company} | *Title:* {job_title}\n*Industry:* {industry} | *Revenue:* ${annual_revenue}\n*Lead Score:* {totalScore}/100 (Behavior: {behaviorScore}, Fit: {fitScore})\n*Trigger:* High-intent activity detected\n*Lifecycle:* Promoted to MQL\n<https://app.hubspot.com/contacts/{portal_id}/contact/{contact_id}|View in HubSpot>",
      ),
      n("6", "delayAction", "Wait 2 hours (SLA response window)", 1240, {
        delayDetails: { duration: 2, unit: "hours" },
      }),
      n(
        "7",
        "hitApi",
        "HubSpot: Update contact score and promote to MQL",
        1480,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"hubspot_score":"{totalScore}","lifecyclestage":"marketingqualifiedlead","hs_lead_status":"New MQL","lead_score_behavior":"{behaviorScore}","lead_score_fit":"{fitScore}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_contact_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log MQL promotion for conversion tracking",
        1720,
        "MQLPromotions",
        '[["{email}","{company}","{job_title}","{totalScore}","{behaviorScore}","{fitScore}","{scoreLabel}","{today}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("4", "8", "output_2"),
    ],
    summary:
      "When a HubSpot contact performs a high-intent action, this workflow fetches their fit data and engagement history, computes a composite lead score (behavior + firmographic fit out of 100), and if the score crosses the 70-point MQL threshold, alerts the sales team via Slack with full context, updates the contact's score and lifecycle stage in HubSpot, and logs the promotion to Google Sheets for conversion quality tracking.",
  },

  // ─── 130 — Demo Request Routing (HubSpot, Pattern K) ───────────────────

  "Demo Request Routing": {
    nodes: [
      trigger(
        "Webhook trigger: Demo request form submitted in HubSpot",
        "webhook_app",
        {
          source: "hubspot",
          event: "form_submission.demo_request",
          howItStarts:
            "Fires when a prospect submits a demo request form in HubSpot, capturing their company details, use case, and preferred meeting time.",
          setup:
            "Connect HubSpot to send a webhook when the demo request form is submitted. The payload includes contact ID, form fields (company size, use case, timeline), and UTM source data.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Enrich contact with company and deal context",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,annualrevenue,numberofemployees,industry,country,hs_analytics_source,recent_conversion_event_name&associations=deals",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "properties.email", customName: "email", type: "string" },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.lastname",
                customName: "last_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              {
                name: "properties.numberofemployees",
                customName: "company_size",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "properties.country",
                customName: "country",
                type: "string",
              },
              {
                name: "properties.annualrevenue",
                customName: "annual_revenue",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compute routing: territory, segment, and AE assignment",
        520,
        {
          codeDetails: {
            language: "javascript",
            code: `// Territory routing based on country and company size
const region = /US|CA/i.test(country || '') ? 'NAM' : (/GB|DE|FR|IT|ES|NL/i.test(country || '') ? 'EMEA' : 'APAC');
const segment = parseInt(company_size || '0') > 500 ? 'Enterprise' : (parseInt(company_size || '0') > 50 ? 'Mid-Market' : 'SMB');
const aeMap = { 'NAM-Enterprise': 'ae-enterprise-nam@company.com', 'NAM-Mid-Market': 'ae-mm-nam@company.com', 'NAM-SMB': 'ae-smb-nam@company.com', 'EMEA-Enterprise': 'ae-enterprise-emea@company.com', 'EMEA-Mid-Market': 'ae-mm-emea@company.com', 'EMEA-SMB': 'ae-smb-emea@company.com', 'APAC-Enterprise': 'ae-enterprise-apac@company.com', 'APAC-Mid-Market': 'ae-mm-apac@company.com', 'APAC-SMB': 'ae-smb-apac@company.com' };
const assignedAE = aeMap[region + '-' + segment] || 'ae-general@company.com';
const priority = segment === 'Enterprise' ? 'High' : (segment === 'Mid-Market' ? 'Medium' : 'Standard');
return { region, segment, assignedAE, priority };`,
          },
        },
      ),
      n("4", "condition", "Enterprise or Mid-Market (priority routing)?", 760, {
        conditionDetails: {
          rules: [
            {
              variable: "segment",
              operator: "is_one_of",
              value: "Enterprise,Mid-Market",
            },
          ],
          combinator: "or",
        },
      }),
      slackNode(
        "5",
        "Slack: Notify assigned AE with demo request details",
        1000,
        "📅 *New Demo Request — {priority} Priority*\n*Prospect:* {first_name} {last_name} ({email})\n*Company:* {company} | *Size:* {company_size} employees\n*Title:* {job_title} | *Industry:* {industry}\n*Region:* {region} | *Segment:* {segment}\n*Assigned AE:* {assignedAE}\n*SLA:* Respond within {priority === 'High' ? '1 hour' : '4 hours'}\n<https://app.hubspot.com/contacts/{portal_id}/contact/{contact_id}|View Contact>",
      ),
      n("6", "delayAction", "Wait 1 hour (response SLA window)", 1240, {
        delayDetails: { duration: 1, unit: "hours" },
      }),
      n(
        "7",
        "hitApi",
        "HubSpot: Update contact owner and create demo task",
        1480,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"hubspot_owner_id":"{ae_owner_id}","lifecyclestage":"salesqualifiedlead","hs_lead_status":"Demo Requested","demo_segment":"{segment}","demo_region":"{region}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_contact_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log demo request routing for SLA tracking",
        1720,
        "DemoRequests",
        '[["{email}","{company}","{segment}","{region}","{assignedAE}","{priority}","{today}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a prospect submits a demo request form in HubSpot, this workflow enriches their profile with company and deal context, computes territory-based routing (NAM/EMEA/APAC) and segment assignment (Enterprise/Mid-Market/SMB), alerts the assigned AE via Slack with priority SLA, updates the contact owner and lifecycle stage in HubSpot, and logs routing decisions to Google Sheets for SLA compliance tracking.",
  },

  // ─── 131 — Meeting Booked Reminder (HubSpot, Pattern I) ────────────────

  "Meeting Booked Reminder": {
    nodes: [
      trigger(
        "Webhook trigger: Meeting booked via HubSpot meetings tool",
        "webhook_app",
        {
          source: "hubspot",
          event: "meeting.booked",
          howItStarts:
            "Fires when a prospect books a meeting through the HubSpot meetings link, capturing meeting details, attendee information, and the associated contact record.",
          setup:
            "Connect HubSpot to send a webhook when a meeting is booked via the meetings tool. The payload includes meeting ID, scheduled datetime, organizer, and contact ID.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract meeting details and generate prep brief",
        280,
        "You are a sales meeting preparation assistant. Extract the meeting details (date, time, type, organizer) and generate a concise prospect prep brief including key talking points, potential pain points based on their industry and role, and 3 discovery questions to ask.",
        "Meeting booked details: {webhook_payload}. Contact: {first_name} {last_name}, {job_title} at {company} in {industry}. Generate meeting prep brief.",
        [
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "meeting_time", as: "meeting_time", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          { field: "talking_points", as: "talking_points", type: "string" },
          {
            field: "discovery_questions",
            as: "discovery_questions",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch contact profile and engagement timeline",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,industry,hs_analytics_num_page_views,recent_conversion_event_name,notes_last_updated",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "properties.email", customName: "email", type: "string" },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.lastname",
                customName: "last_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Is the meeting more than 24 hours away?", 760, {
        conditionDetails: {
          rules: [
            {
              variable: "hours_until_meeting",
              operator: "greater_than",
              value: "24",
            },
          ],
          combinator: "and",
        },
      }),
      emailNode(
        "5",
        "Email: Meeting confirmation with agenda and prep resources",
        1000,
        "{email}",
        "Your meeting is confirmed for {meeting_date}, {first_name}",
        "Hi {first_name},\n\nGreat news — your {meeting_type} is confirmed!\n\n📅 Date: {meeting_date}\n🕐 Time: {meeting_time}\n📍 Join link: [Meeting Link]\n\nTo make the most of our time together, here's what we'll cover:\n- Understanding your current challenges at {company}\n- How teams in {industry} are solving similar problems\n- A personalized walkthrough based on your use case\n\nTo help us prepare, feel free to jot down any specific questions or scenarios you'd like us to address.\n\n[Add to Calendar] | [Reschedule if Needed]\n\nLooking forward to connecting!\n\nBest,\nThe Sales Team",
      ),
      n("6", "delayAction", "Wait until 2 hours before meeting", 1240, {
        delayDetails: { duration: 22, unit: "hours" },
      }),
      n(
        "7",
        "condition",
        "Is the meeting still active (not cancelled)?",
        1480,
        {
          conditionDetails: {
            rules: [
              {
                variable: "meeting_cancelled",
                operator: "not_equals",
                value: "true",
              },
            ],
            combinator: "and",
          },
        },
      ),
      emailNode(
        "8",
        "Email: Day-of reminder with join link and quick prep",
        1720,
        "{email}",
        "Reminder: Your meeting is in 2 hours, {first_name}",
        "Hi {first_name},\n\nJust a friendly reminder — your {meeting_type} is starting in about 2 hours.\n\n🕐 Time: {meeting_time}\n📍 Join here: [Meeting Link]\n\nQuick prep checklist:\n✅ Have your current workflow or process top of mind\n✅ Think about what success looks like for {company}\n✅ Bring any questions about implementation or timeline\n\nWe're excited to chat. See you soon!\n\nBest,\nThe Sales Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "When a prospect books a meeting via HubSpot's meetings tool, this workflow uses AI to extract meeting details and generate a prep brief, fetches the contact's profile and engagement history, sends a confirmation email with agenda and prep resources, then delivers a day-of reminder 2 hours before the meeting with the join link and a quick prep checklist to maximize attendance and meeting quality.",
  },

  // ─── 132 — Meeting No-Show Recovery (HubSpot, Pattern G) ───────────────

  "Meeting No-Show Recovery": {
    nodes: [
      trigger(
        "Schedule trigger: Check for meeting no-shows hourly",
        "schedule",
        {
          schedule: "every 1 hour",
          howItStarts:
            "Runs hourly to identify HubSpot meetings where the scheduled time has passed but the prospect did not join, triggering a recovery sequence to reschedule.",
          setup:
            "Configure a scheduled trigger to run every hour during business hours. The workflow queries HubSpot for meetings that ended without attendee participation.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Lookup no-show contact and meeting details",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,industry,hs_analytics_num_visits,hubspot_owner_id,notes_last_updated",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "properties.email", customName: "email", type: "string" },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.lastname",
                customName: "last_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "properties.hubspot_owner_id",
                customName: "owner_id",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze likely no-show reason and recovery approach",
        520,
        "You are a sales recovery strategist. Given a meeting no-show, analyze the likely reason (scheduling conflict, lost interest, forgot, timezone confusion) based on the contact's engagement history and meeting context. Recommend a recovery_approach (empathetic_reschedule, value_reminder, alternative_format), suggested_subject_line, and tone.",
        "No-show for meeting with {first_name} {last_name}, {job_title} at {company}. Industry: {industry}. Meeting type: {meeting_type}. Engagement level: {engagement_level}. Analyze and recommend recovery approach.",
        [
          { field: "likely_reason", as: "likely_reason", type: "string" },
          {
            field: "recovery_approach",
            as: "recovery_approach",
            type: "string",
          },
          {
            field: "suggested_subject",
            as: "suggested_subject",
            type: "string",
          },
          { field: "tone", as: "recovery_tone", type: "string" },
        ],
      ),
      n(
        "4",
        "condition",
        "Is this a high-value prospect (enterprise or active lead)?",
        760,
        {
          conditionDetails: {
            rules: [
              {
                variable: "company_size",
                operator: "greater_than",
                value: "200",
              },
            ],
            combinator: "or",
          },
        },
      ),
      emailNode(
        "5",
        "Email: Empathetic no-show recovery with easy reschedule link",
        1000,
        "{email}",
        "We missed you, {first_name} — easy reschedule link inside",
        "Hi {first_name},\n\nIt looks like we weren't able to connect for our scheduled meeting — no worries at all! We know things come up.\n\nI've put together a fresh scheduling link with more time options to make it easy to find a slot that works better:\n\n[Pick a New Time — Takes 30 Seconds]\n\nIf a live meeting isn't ideal right now, I also have a few alternatives:\n- 📹 [Watch a 5-minute product overview video]\n- 📄 [Download our interactive product tour]\n- 💬 [Chat with us live on our website]\n\nWe're flexible — whatever works best for you and the team at {company}.\n\nBest,\nThe Sales Team",
      ),
      n("6", "delayAction", "Wait 3 days for reschedule", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Has prospect rescheduled or responded?", 1480, {
        conditionDetails: {
          rules: [
            {
              variable: "meeting_rescheduled",
              operator: "equals",
              value: "true",
            },
          ],
          combinator: "or",
        },
      }),
      emailNode(
        "8",
        "Email: Second recovery with alternative engagement options",
        1720,
        "{email}",
        "Still interested, {first_name}? Here's another way to connect",
        "Hi {first_name},\n\nI wanted to follow up one more time — I know scheduling can be tricky, especially in {industry}.\n\nIf a meeting isn't the right next step, here are a couple of async options that might be more convenient:\n\n1. **Self-guided product tour** — explore at your own pace: [Start Tour]\n2. **ROI calculator** — see potential impact for {company}: [Calculate ROI]\n3. **Customer story** — how a similar {industry} company got results: [Read Case Study]\n\nAnd of course, the scheduling link is still open whenever you're ready:\n[Book a Time]\n\nNo pressure — just want to make sure you have everything you need.\n\nBest,\nThe Sales Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Runs hourly to detect meeting no-shows in HubSpot, then uses AI to analyze the likely reason and best recovery approach for each contact. Sends an empathetic recovery email with an easy reschedule link and alternative engagement formats (video, product tour, live chat), waits 3 days, and if no reschedule occurs, sends a second attempt with async options (self-guided tour, ROI calculator, case study) to re-engage the prospect.",
  },

  // ─── 133 — Content Download Thank-You and Nurture (HubSpot, Pattern I) ──

  "Content Download Thank-You and Nurture": {
    nodes: [
      trigger(
        "Webhook trigger: Content downloaded via HubSpot form",
        "webhook_app",
        {
          source: "hubspot",
          event: "form_submission.content_download",
          howItStarts:
            "Fires when a contact downloads gated content (ebook, whitepaper, template, checklist) through a HubSpot landing page form, triggering a thank-you and nurture sequence.",
          setup:
            "Connect HubSpot to send a webhook on content download form submissions. The payload includes contact ID, content asset name, form ID, and page URL.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Analyze downloaded content and map to nurture path",
        280,
        "You are a content marketing strategist. Given the downloaded asset name and topic, determine the content_category (awareness, consideration, decision), buyer_persona (executive, practitioner, technical), related_topics (array of 3 next-best content topics), and recommended_cta (webinar, demo, consultation, deeper_content).",
        "Downloaded content: {asset_name}. Page URL: {page_url}. Analyze content category, buyer persona, and recommend next nurture steps.",
        [
          { field: "content_category", as: "content_category", type: "string" },
          { field: "buyer_persona", as: "buyer_persona", type: "string" },
          { field: "related_topics", as: "related_topics", type: "object" },
          { field: "recommended_cta", as: "recommended_cta", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch contact profile and prior downloads",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,company,industry,jobtitle,lifecyclestage,hs_analytics_num_page_views,num_conversion_events,recent_conversion_event_name",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "properties.email", customName: "email", type: "string" },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              {
                name: "properties.num_conversion_events",
                customName: "total_downloads",
                type: "number",
              },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "First-time downloader or returning lead?", 760, {
        conditionDetails: {
          rules: [
            {
              variable: "total_downloads",
              operator: "greater_than",
              value: "1",
            },
          ],
          combinator: "and",
        },
      }),
      emailNode(
        "5",
        "Email: Thank-you with download link and related resources",
        1000,
        "{email}",
        "Your download is ready, {first_name} — plus 3 bonus resources",
        "Hi {first_name},\n\nThanks for downloading our {asset_name}! Here's your direct access link:\n\n📥 [Download {asset_name}]\n\nSince you're interested in this topic, we thought you'd also find these resources valuable:\n\n1. [{related_topic_1}] — A deeper dive into the strategy behind this topic\n2. [{related_topic_2}] — Practical templates and frameworks you can use today\n3. [{related_topic_3}] — Real-world examples from {industry} companies\n\nThese resources together give you a comprehensive playbook for tackling this challenge at {company}.\n\nHappy reading!\nThe Content Team",
      ),
      n("6", "delayAction", "Wait 3 days", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Has contact engaged with follow-up content?", 1480, {
        conditionDetails: {
          rules: [
            { variable: "email_clicked", operator: "equals", value: "true" },
          ],
          combinator: "or",
        },
      }),
      emailNode(
        "8",
        "Email: Progressive nurture pushing toward conversion CTA",
        1720,
        "{email}",
        "{first_name}, ready to put these insights into action?",
        "Hi {first_name},\n\nYou've been exploring some of our best content on this topic, and we wanted to help you take the next step.\n\nBased on what you've been reading, here's what teams like yours at {company} typically do next:\n\n🎯 **For practitioners:** [Join our upcoming workshop — hands-on implementation]\n📊 **For decision-makers:** [See the ROI calculator — model impact for {company}]\n🤝 **Ready to talk?** [Book a 15-minute strategy call with our team]\n\nWe've helped hundreds of {industry} companies turn these insights into measurable results. We'd love to help {company} do the same.\n\nBest,\nThe Marketing Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
    summary:
      "When a contact downloads gated content from a HubSpot landing page, this workflow uses AI to categorize the content and map it to a nurture path, fetches the contact's profile and download history, sends a thank-you email with the download link plus 3 related resources, waits 3 days, then delivers a progressive conversion-focused email with persona-appropriate CTAs (workshop, ROI calculator, or strategy call).",
  },

  // ─── 134 — Webinar Invitation Workflow (HubSpot, Pattern I) ─────────────

  "Webinar Invitation Workflow": {
    nodes: [
      trigger(
        "Webhook trigger: New webinar created or segment matched in HubSpot",
        "webhook_app",
        {
          source: "hubspot",
          event: "list.member_added.webinar_target",
          howItStarts:
            "Fires when a contact is added to a HubSpot smart list matching the target audience for an upcoming webinar, based on industry, lifecycle stage, and content interest signals.",
          setup:
            "Connect HubSpot to send a webhook when contacts are added to the webinar target list. The list is built using smart criteria: matching industry, lifecycle stage >= lead, and relevant topic engagement.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Generate personalized webinar pitch for contact",
        280,
        "You are a webinar marketing specialist. Given the webinar topic, speaker, and the contact's industry and role, generate a personalized value_proposition (why this webinar matters to them specifically), key_takeaways (3 bullet points), and urgency_hook (limited seats, exclusive content, live Q&A).",
        "Webinar: {webinar_title} with {speaker_name}. Contact: {first_name}, {job_title} at {company} in {industry}. Generate personalized webinar pitch.",
        [
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          { field: "urgency_hook", as: "urgency_hook", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch contact details and webinar history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,industry,lifecyclestage,hs_email_last_open_date,num_conversion_events",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "properties.email", customName: "email", type: "string" },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is contact an active lead or MQL (engaged audience)?",
        760,
        {
          conditionDetails: {
            rules: [
              {
                variable: "lifecycle_stage",
                operator: "is_one_of",
                value: "lead,marketingqualifiedlead",
              },
            ],
            combinator: "or",
          },
        },
      ),
      emailNode(
        "5",
        "Email: Personalized webinar invitation with registration link",
        1000,
        "{email}",
        "{first_name}, you're invited: {webinar_title}",
        "Hi {first_name},\n\n{value_proposition}\n\nWe're hosting a live webinar that's especially relevant for {job_title}s in {industry}:\n\n📅 **{webinar_title}**\n🎤 Featuring: {speaker_name}\n🕐 Date & Time: {webinar_date} at {webinar_time}\n\n**What you'll learn:**\n{key_takeaways}\n\n{urgency_hook}\n\n[Register Now — Save Your Spot]\n\nCan't make it live? Register anyway and we'll send you the recording.\n\nSee you there!\nThe Events Team",
      ),
      n("6", "delayAction", "Wait 3 days before reminder", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Has contact registered for the webinar?", 1480, {
        conditionDetails: {
          rules: [
            {
              variable: "webinar_registered",
              operator: "equals",
              value: "true",
            },
          ],
          combinator: "or",
        },
      }),
      emailNode(
        "8",
        "Email: Reminder for non-registrants with social proof",
        1720,
        "{email}",
        "Last chance: {webinar_title} is filling up, {first_name}",
        'Hi {first_name},\n\nQuick reminder — our upcoming webinar on {webinar_title} already has over 500 registrants from companies like yours in {industry}.\n\nHere\'s what attendees from past sessions have said:\n\n⭐ "The most actionable webinar I\'ve attended this year" — VP Marketing, SaaS Company\n⭐ "Changed how our team approaches this challenge" — Director of Ops, Enterprise Co.\n\nThere are still a few spots available, but registration closes soon.\n\n[Register Before Spots Fill Up]\n\nDon\'t miss this one!\n\nBest,\nThe Events Team',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "When a contact matches a webinar target audience list in HubSpot, this workflow generates a personalized webinar pitch using AI, fetches the contact's engagement profile, sends a tailored invitation email with key takeaways and a registration link, waits 3 days, and sends a social-proof-driven reminder to non-registrants to maximize webinar attendance.",
  },

  // ─── 135 — Webinar Attendee Follow-Up (HubSpot, Pattern I) ─────────────

  "Webinar Attendee Follow-Up": {
    nodes: [
      trigger(
        "Webhook trigger: Webinar attendance confirmed in HubSpot",
        "webhook_app",
        {
          source: "hubspot",
          event: "webinar.attendee_confirmed",
          howItStarts:
            "Fires when a contact is marked as having attended a webinar (via Zoom integration or manual import), triggering a personalized follow-up sequence with recording and next steps.",
          setup:
            "Connect HubSpot to receive Zoom webinar attendance data or manual attendance imports. The webhook fires when attendance status changes to 'attended' with duration and engagement data.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Generate personalized follow-up based on webinar topic",
        280,
        "You are a webinar follow-up strategist. Given the webinar title, topic, and the attendee's role and industry, generate a personalized_recap (2-3 sentences summarizing key points relevant to their role), next_resource (the single best next content piece), and conversion_cta (demo, trial, consultation, or deeper content based on their lifecycle stage).",
        "Webinar: {webinar_title}. Attendee: {first_name}, {job_title} at {company} in {industry}. Attendance duration: {attendance_duration} minutes. Lifecycle stage: {lifecycle_stage}. Generate personalized follow-up.",
        [
          {
            field: "personalized_recap",
            as: "personalized_recap",
            type: "string",
          },
          { field: "next_resource", as: "next_resource", type: "string" },
          { field: "conversion_cta", as: "conversion_cta", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch attendee profile and engagement data",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,industry,lifecyclestage,hubspot_score,num_conversion_events",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "properties.email", customName: "email", type: "string" },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
              {
                name: "properties.hubspot_score",
                customName: "lead_score",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Attended 75%+ of webinar (high engagement)?", 760, {
        conditionDetails: {
          rules: [
            {
              variable: "attendance_percentage",
              operator: "greater_than",
              value: "75",
            },
          ],
          combinator: "and",
        },
      }),
      emailNode(
        "5",
        "Email: Attendee follow-up with recording and next resource",
        1000,
        "{email}",
        "Thanks for attending, {first_name} — recording + next steps inside",
        "Hi {first_name},\n\nThanks for joining us for {webinar_title}! Here's everything you need:\n\n🎥 **Watch the recording:** [Full Webinar Replay]\n📄 **Download the slides:** [Presentation Deck PDF]\n\n**Your personalized recap:**\n{personalized_recap}\n\n**Recommended next step for you:**\n{next_resource}\n\nThis resource builds directly on what was covered in the webinar and will help you apply these insights at {company}.\n\n{conversion_cta}\n\nHave questions about anything covered? Just reply to this email.\n\nBest,\nThe Events Team",
      ),
      n("6", "delayAction", "Wait 4 days", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Has attendee engaged with follow-up content?",
        1480,
        {
          conditionDetails: {
            rules: [
              { variable: "email_clicked", operator: "equals", value: "true" },
            ],
            combinator: "or",
          },
        },
      ),
      emailNode(
        "8",
        "Email: Progressive content toward meeting or demo",
        1720,
        "{email}",
        "{first_name}, turn webinar insights into results at {company}",
        "Hi {first_name},\n\nSince attending {webinar_title}, you've been exploring some great resources. Here's the next step to turn those insights into real results at {company}:\n\n📊 **Case Study:** See how a {industry} company implemented the exact strategies from the webinar and achieved measurable ROI within 90 days.\n[Read the Full Case Study]\n\n🤝 **Ready to discuss your specific situation?**\nOur team has helped dozens of {industry} companies apply these strategies. Book a quick call and we'll map out what implementation could look like for {company}.\n\n[Book a Strategy Call] | [Explore the Product Tour]\n\nBest,\nThe Marketing Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
    summary:
      "When a webinar attendee is confirmed in HubSpot, this workflow uses AI to generate a personalized recap and next-step recommendation based on their role and industry, sends a follow-up email with the recording, slides, and tailored next resource, waits 4 days, then delivers a progressive conversion email with a case study and meeting CTA to move attendees from content engagement toward a sales conversation.",
  },

  // ─── 136 — Webinar No-Show Replay Flow (HubSpot, Pattern I) ────────────

  "Webinar No-Show Replay Flow": {
    nodes: [
      trigger(
        "Webhook trigger: Registrant marked no-show in HubSpot",
        "webhook_app",
        {
          source: "hubspot",
          event: "webinar.registrant_no_show",
          howItStarts:
            "Fires when a webinar registrant is marked as a no-show after the event ends — they registered but did not attend, triggering a replay delivery and re-engagement flow.",
          setup:
            "Connect HubSpot to receive Zoom attendance data post-webinar. The webhook fires for contacts with registration status but no attendance record after the event concludes.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Generate compelling replay pitch for no-show",
        280,
        "You are a webinar re-engagement specialist. Given the webinar title, key topics covered, and the registrant's role, generate a fomo_hook (what they missed, framed positively), highlight_summary (3 best moments from the webinar), and replay_cta (compelling reason to watch the recording now).",
        "Webinar: {webinar_title}. No-show: {first_name}, {job_title} at {company}. Key topics covered: {webinar_topics}. Generate a compelling replay pitch.",
        [
          { field: "fomo_hook", as: "fomo_hook", type: "string" },
          {
            field: "highlight_summary",
            as: "highlight_summary",
            type: "string",
          },
          { field: "replay_cta", as: "replay_cta", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Fetch no-show contact profile", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,industry,lifecyclestage,hs_email_last_open_date",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            { name: "properties.email", customName: "email", type: "string" },
            {
              name: "properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "properties.company",
              customName: "company",
              type: "string",
            },
            {
              name: "properties.jobtitle",
              customName: "job_title",
              type: "string",
            },
            {
              name: "properties.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
          ],
        },
      }),
      n(
        "4",
        "condition",
        "Is contact in an active lifecycle stage (lead or MQL)?",
        760,
        {
          conditionDetails: {
            rules: [
              {
                variable: "lifecycle_stage",
                operator: "is_one_of",
                value: "lead,marketingqualifiedlead,salesqualifiedlead",
              },
            ],
            combinator: "or",
          },
        },
      ),
      emailNode(
        "5",
        "Email: Replay link with highlights of what they missed",
        1000,
        "{email}",
        "You missed a great one, {first_name} — here's the replay",
        "Hi {first_name},\n\nWe noticed you couldn't make it to {webinar_title} — no worries! Life happens. But you definitely don't want to miss what was covered.\n\n{fomo_hook}\n\n**Top moments from the session:**\n{highlight_summary}\n\n🎥 **Watch the full replay at your convenience:**\n[Watch Recording Now]\n\nThe recording is available for the next 14 days, so you can watch whenever works best. Pro tip: watch at 1.5x speed to get all the insights in 30 minutes.\n\n📄 [Download the slide deck] for quick reference.\n\nBest,\nThe Events Team",
      ),
      n("6", "delayAction", "Wait 5 days", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Has no-show watched the replay?", 1480, {
        conditionDetails: {
          rules: [
            { variable: "replay_viewed", operator: "equals", value: "true" },
          ],
          combinator: "or",
        },
      }),
      emailNode(
        "8",
        "Email: Final replay nudge with key takeaway summary",
        1720,
        "{email}",
        "Last chance: {webinar_title} replay expires soon, {first_name}",
        "Hi {first_name},\n\nThe replay for {webinar_title} expires in a few days, and we wanted to make sure you don't miss out.\n\nIf you're short on time, here's the 2-minute summary:\n\n📋 **Key Takeaway #1:** [Most impactful insight from the webinar]\n📋 **Key Takeaway #2:** [Actionable framework discussed]\n📋 **Key Takeaway #3:** [Data point that surprised attendees]\n\nWant the full picture? The recording is still available:\n[Watch Before It Expires]\n\nAlternatively, if you'd prefer to discuss these topics 1-on-1 in the context of {company}, our team would love to chat:\n[Book a Quick Call]\n\nBest,\nThe Events Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "When a webinar registrant is marked as a no-show in HubSpot, this workflow uses AI to generate a compelling replay pitch with highlights and FOMO hooks, fetches the contact's profile, sends an empathetic email with the replay link and slide deck, waits 5 days, and if they haven't watched, sends a final nudge with a 2-minute key takeaway summary and an expiration deadline to drive replay consumption.",
  },

  // ─── 137 — Blog Subscriber to MQL Nurture (HubSpot, Pattern I) ─────────

  "Blog Subscriber to MQL Nurture": {
    nodes: [
      trigger(
        "Webhook trigger: New blog subscriber in HubSpot",
        "webhook_app",
        {
          source: "hubspot",
          event: "form_submission.blog_subscribe",
          howItStarts:
            "Fires when a visitor subscribes to the company blog through a HubSpot form, entering the top-of-funnel nurture path designed to move blog readers toward MQL status.",
          setup:
            "Connect HubSpot to send a webhook when the blog subscription form is submitted. The payload includes contact ID, blog category subscribed to, and referring page URL.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Analyze subscriber interest and map nurture path",
        280,
        "You are a content-to-pipeline strategist. Given the blog category, referring page, and subscriber context, determine the primary_interest_topic, content_maturity (beginner, intermediate, advanced), recommended_content_sequence (array of 3 content pieces progressing from educational to evaluative), and mql_signal_to_watch (the engagement behavior that indicates readiness for sales).",
        "Blog subscription: category {blog_category}, referring page {referring_url}. Subscriber: {first_name} at {company} in {industry}. Determine nurture path to MQL.",
        [
          {
            field: "primary_interest_topic",
            as: "primary_interest_topic",
            type: "string",
          },
          { field: "content_maturity", as: "content_maturity", type: "string" },
          {
            field: "recommended_content_sequence",
            as: "content_sequence",
            type: "object",
          },
          { field: "mql_signal_to_watch", as: "mql_signal", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch subscriber profile and engagement baseline",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,company,industry,jobtitle,lifecyclestage,hs_analytics_num_page_views,hs_analytics_num_visits,num_conversion_events",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "properties.email", customName: "email", type: "string" },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
              {
                name: "properties.hs_analytics_num_page_views",
                customName: "page_views",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is subscriber already a known lead with engagement?",
        760,
        {
          conditionDetails: {
            rules: [
              { variable: "page_views", operator: "greater_than", value: "5" },
            ],
            combinator: "and",
          },
        },
      ),
      emailNode(
        "5",
        "Email: Welcome to the blog with curated best-of content",
        1000,
        "{email}",
        "Welcome to the blog, {first_name} — your curated reading list",
        "Hi {first_name},\n\nThanks for subscribing to our blog! You'll get fresh insights on {primary_interest_topic} delivered to your inbox every week.\n\nTo get you started, here are our most popular posts on this topic:\n\n📖 **Must-read #1:** [{content_piece_1}] — The foundational guide everyone references\n📖 **Must-read #2:** [{content_piece_2}] — Practical tactics you can implement this week\n📖 **Must-read #3:** [{content_piece_3}] — Advanced strategies from industry leaders\n\nThese three articles together give you a complete picture of {primary_interest_topic} — from basics to advanced implementation.\n\nYou can also [browse all posts by category] to find exactly what interests you.\n\nHappy reading!\nThe Content Team",
      ),
      n("6", "delayAction", "Wait 5 days", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Has subscriber engaged with 2+ blog posts or downloaded content?",
        1480,
        {
          conditionDetails: {
            rules: [
              {
                variable: "blog_posts_read",
                operator: "greater_than",
                value: "1",
              },
            ],
            combinator: "or",
          },
        },
      ),
      emailNode(
        "8",
        "Email: Deeper content offer to advance toward MQL",
        1720,
        "{email}",
        "{first_name}, ready for the deep dive? Exclusive resource inside",
        "Hi {first_name},\n\nYou've been diving deep into {primary_interest_topic} — love to see it! Based on what you've been reading, we think you're ready for the next level.\n\n🎯 **Exclusive resource for engaged readers like you:**\n[Download: The Complete {primary_interest_topic} Playbook]\n\nThis isn't a typical blog post — it's a 40-page guide with:\n- Data-driven benchmarks for {industry} companies\n- A step-by-step implementation framework\n- ROI modeling templates you can use at {company}\n- Expert interviews with practitioners in your space\n\n[Download the Playbook Now]\n\nThis is the resource that's helped hundreds of teams move from reading to results.\n\nBest,\nThe Content Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
    summary:
      "When a visitor subscribes to the blog in HubSpot, this workflow uses AI to map their interest topic to an MQL nurture path, fetches their engagement baseline, sends a welcome email with curated best-of content for their topic, waits 5 days, then advances engaged subscribers toward MQL with a high-value gated resource (playbook with benchmarks, frameworks, and ROI templates) to convert readers into qualified leads.",
  },

  // ─── 138 — Contact-Us Inquiry Routing (HubSpot, Pattern K) ─────────────

  "Contact-Us Inquiry Routing": {
    nodes: [
      trigger(
        "Webhook trigger: Contact-us form submitted in HubSpot",
        "webhook_app",
        {
          source: "hubspot",
          event: "form_submission.contact_us",
          howItStarts:
            "Fires when a prospect or customer submits the Contact Us form in HubSpot, capturing their inquiry type, message, and contact details for intelligent routing.",
          setup:
            "Connect HubSpot to send a webhook on Contact Us form submissions. The payload includes contact ID, form fields (inquiry type, message, company), and submission page URL.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Enrich contact with company and deal data",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,numberofemployees,annualrevenue,industry,country,lifecyclestage,hs_lead_status,hubspot_owner_id&associations=deals",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "properties.email", customName: "email", type: "string" },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.lastname",
                customName: "last_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              {
                name: "properties.numberofemployees",
                customName: "company_size",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "properties.country",
                customName: "country",
                type: "string",
              },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
              {
                name: "properties.hubspot_owner_id",
                customName: "existing_owner",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compute inquiry routing: department, priority, SLA",
        520,
        {
          codeDetails: {
            language: "javascript",
            code: `// Route by inquiry type and contact context
const inquiryType = (inquiry_type || 'general').toLowerCase();
const isCustomer = lifecycle_stage === 'customer';
const isEnterprise = parseInt(company_size || '0') > 500;
let department, assignee, slaHours;
if (/support|bug|issue|problem|help/.test(inquiryType)) {
  department = 'Support'; assignee = isEnterprise ? 'support-enterprise@company.com' : 'support-general@company.com'; slaHours = isEnterprise ? 2 : 8;
} else if (/pricing|quote|buy|purchase|plan/.test(inquiryType)) {
  department = 'Sales'; assignee = isEnterprise ? 'ae-enterprise@company.com' : 'ae-growth@company.com'; slaHours = isEnterprise ? 1 : 4;
} else if (/partner|integration|api/.test(inquiryType)) {
  department = 'Partnerships'; assignee = 'partnerships@company.com'; slaHours = 24;
} else {
  department = 'General'; assignee = 'hello@company.com'; slaHours = 12;
}
const priority = isEnterprise ? 'High' : (isCustomer ? 'Medium' : 'Standard');
return { department, assignee, slaHours, priority, isCustomer, isEnterprise };`,
          },
        },
      ),
      n("4", "condition", "Is this a sales or pricing inquiry?", 760, {
        conditionDetails: {
          rules: [
            { variable: "department", operator: "equals", value: "Sales" },
          ],
          combinator: "and",
        },
      }),
      slackNode(
        "5",
        "Slack: Route inquiry to correct department with context",
        1000,
        "📨 *New Contact-Us Inquiry — {priority} Priority*\n*From:* {first_name} {last_name} ({email})\n*Company:* {company} | *Size:* {company_size} employees\n*Title:* {job_title} | *Industry:* {industry}\n*Inquiry Type:* {inquiry_type} → *Routed to:* {department}\n*Assigned:* {assignee}\n*SLA:* Respond within {slaHours} hours\n*Customer:* {isCustomer ? 'Yes' : 'No'}\n*Message:* {inquiry_message}\n<https://app.hubspot.com/contacts/{portal_id}/contact/{contact_id}|View in HubSpot>",
      ),
      n("6", "delayAction", "Wait for SLA window (4 hours)", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      n(
        "7",
        "hitApi",
        "HubSpot: Update contact owner and log inquiry routing",
        1480,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"hubspot_owner_id":"{owner_id}","hs_lead_status":"Open Inquiry","inquiry_type":"{inquiry_type}","inquiry_department":"{department}","inquiry_priority":"{priority}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_contact_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log inquiry routing for SLA compliance",
        1720,
        "InquiryRouting",
        '[["{email}","{company}","{inquiry_type}","{department}","{assignee}","{priority}","{slaHours}","{today}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a Contact Us form is submitted in HubSpot, this workflow enriches the contact with company and deal data, computes intelligent routing based on inquiry type and company segment (support, sales, partnerships, or general), assigns priority and SLA based on enterprise status, alerts the routed department via Slack with full context, updates the contact owner and inquiry metadata in HubSpot, and logs routing decisions to Google Sheets for SLA compliance tracking.",
  },
};
